/**
 * Client-side wrappers around the billing Edge Functions.
 *
 * Pattern: every call requires a logged-in user; the user's JWT is sent in the
 * Authorization header. Server functions read auth.uid() from it and apply RLS.
 */

import { supabase, isSupabaseConfigured } from "./supabase";

export type Plan = "starter" | "pro" | "business";
export type CreditPack = "quick" | "standard" | "bulk";

export const CREDIT_PACKS: Record<CreditPack, { credits: number; priceUsd: number; displayName: string; perDoc: number }> = {
  quick:    { credits: 10,  priceUsd: 5,  displayName: "Quick pack",    perDoc: 0.50 },
  standard: { credits: 50,  priceUsd: 20, displayName: "Standard pack", perDoc: 0.40 },
  bulk:     { credits: 500, priceUsd: 79, displayName: "Bulk pack",     perDoc: 0.158 },
};

export class BillingError extends Error {
  code: string;
  constructor(code: string, message: string) {
    super(message);
    this.code = code;
    this.name = "BillingError";
  }
}

async function callFunction<T>(name: string, body?: unknown): Promise<T> {
  if (!isSupabaseConfigured) {
    throw new BillingError("not_configured", "Supabase is not configured");
  }
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData.session?.access_token;
  if (!accessToken) {
    throw new BillingError("not_authenticated", "Sign in to manage billing");
  }

  const url = import.meta.env.VITE_SUPABASE_URL as string;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

  const response = await fetch(`${url}/functions/v1/${name}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      apikey: anonKey,
      "Content-Type": "application/json",
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  const json = await response.json().catch(() => null);
  if (!response.ok || !json) {
    const code = json?.error?.code ?? "request_failed";
    const message = json?.error?.message ?? `Request failed with status ${response.status}`;
    throw new BillingError(code, message);
  }
  return json as T;
}

/**
 * Start a Stripe Checkout flow for the given plan. The browser is redirected to
 * Stripe's hosted checkout page — DocLens never sees the card.
 */
export async function startCheckout(plan: Plan): Promise<void> {
  const { url } = await callFunction<{ url: string }>("create-checkout-session", { plan });
  window.location.href = url;
}

/**
 * Open Stripe's Customer Portal for managing the current subscription.
 */
export async function openBillingPortal(): Promise<void> {
  const { url } = await callFunction<{ url: string }>("create-portal-session");
  window.location.href = url;
}

/**
 * Buy a one-time credit pack. Browser is redirected to Stripe Checkout in
 * mode=payment. On success, the stripe-webhook adds the credits to the user's
 * account; the user lands on /billing?status=credits_success.
 */
export async function startCreditPackPurchase(pack: CreditPack): Promise<void> {
  const { url } = await callFunction<{ url: string }>("purchase-credits", { pack });
  window.location.href = url;
}

/**
 * Read the user's current credit balance. Returns 0 if no ledger rows exist.
 */
export async function getCreditBalance(): Promise<number> {
  if (!isSupabaseConfigured) return 0;
  const { data: sessionData } = await supabase.auth.getSession();
  if (!sessionData.session) return 0;

  // The credit_balances view is RLS-gated by account ownership through credit_ledger
  const { data, error } = await supabase
    .from("credit_balances")
    .select("balance")
    .maybeSingle();

  if (error) {
    console.warn("[billing] getCreditBalance failed:", error);
    return 0;
  }
  return (data?.balance as number | undefined) ?? 0;
}
